/**
 * Module dependencies.
 */

import { FormList } from 'app/components/organisms/crud/form-types';
import { requiredRule } from 'app/core/utils/field-rules';
import { usePaginatedCrudRequest } from 'app/hooks/app-requests/crud/use-paginated-crud-request';
import { TFunction } from 'react-i18next';
import { RenderUserItem } from './pharmacies/render-user-item';
import { User } from 'app/types/user';
import React from 'react';

/**
 * Left.
 */

const left = (translate: TFunction, type: 'add' | 'edit'): FormList => [
  {
    type: 'inputField',
    name: 'name',
    label: translate('common.labels.name'),
    rules: requiredRule(translate)
  },
  {
    type: 'selectField',
    name: 'userId',
    disabled: type === 'edit',
    showSearch: true,
    allowClear: true,
    rules: requiredRule(translate),
    label: translate('common.labels.user'),
    paginated: { enabled: true, pageSize: 50 },
    options: {
      hook: usePaginatedCrudRequest as any,
      hookProps: (_values: any, _params: any, options: any) => {
        const searchValue = options?.searchValue?.trim();
        return [
          {
            key: ['users-paginated', searchValue || ''],
            endpoint: 'users' as any,
            options: {
              ...(searchValue ? { search: searchValue } : {})
            },
            pageSize: 50
          }
        ];
      },
      normalize: (items: User[]) => {
        if (!items || !Array.isArray(items)) {
          return [];
        }
        const result = items
          .filter(item => item && item.id && !!item.phoneNumber)
          .map(item => ({
            value: item.id,
            label: <RenderUserItem item={item} />,
            searchValue: item.name
          }));

        return result;
      }
    }
  },
  {
    type: 'selectField',
    name: 'pharmacyId',
    disabled: type === 'edit',
    showSearch: true,
    allowClear: true,
    rules: requiredRule(translate),
    label: translate('common.labels.pharmacy'),
    paginated: { enabled: true, pageSize: 50 },
    options: {
      hook: usePaginatedCrudRequest as any,
      hookProps: (_values: any, _params: any, options: any) => {
        const searchValue = options?.searchValue?.trim();
        return [
          {
            key: ['pharmacies-paginated', searchValue || ''],
            endpoint: 'pharmacies' as any,
            options: {
              ...(searchValue ? { search: searchValue } : {})
            },
            pageSize: 50
          }
        ];
      },
      normalize: (items: User[]) => {
        if (!items || !Array.isArray(items)) {
          return [];
        }
        const result = items
          .filter(item => item && item.id)
          .map(item => ({
            value: item.id,
            label: <RenderUserItem item={item} />,
            searchValue: item.name
          }));

        return result;
      }
    }
  }
];

/**
 * Right.
 */

const right = (translate: TFunction): FormList => [
  {
    type: 'inputField',
    name: 'id',
    disabled: true,
    label: translate('common.labels.id'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'createdAt',
    disabled: true,
    label: translate('common.labels.createdAt'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'updatedAt',
    disabled: true,
    label: translate('common.labels.updatedAt'),
    rules: undefined
  }
];

/**
 * Export `formFields`.
 */

export const formFields = (translate: TFunction, type: 'add' | 'edit'): FormList => [
  {
    type: 'columns',
    gutter: 40,
    name: 'mainWire',
    columns: [
      {
        column: 18,
        children: left(translate, type)
      },
      {
        column: 6,
        children: right(translate)
      }
    ]
  }
];
